import { useEffect, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AxiosError } from 'axios';
import { apiClient } from '../../api/client';
import { useAuthStore, type AuthUser } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import { useDeployMode } from '../../hooks/useDeployMode';
import { tOss } from '../../lib/oss-i18n';
import {
  AlertTriangle, AtSign, Eye, EyeOff, Key, Loader2, Lock,
  MessageSquare, Moon, Sun, Info,
} from '../../icons';
import { getSharedLang, type Lang } from '../../lib/base-i18n';
import {
  peekSessionEndReason, clearSessionEndReason, type SessionEndReason,
} from '../../lib/session-end-reason';
import { LOGIN_DEMO_HTML } from './loginDemo';
import './LoginPage.css';

// Cycle 52 — LoginPage 의 폼/에러 부분 i18n.
// Cycle 54 — 마케팅 텍스트 (title + subtitle + 3 feature) i18n 확장.
// cycle 49 MfaVerifyPage / cycle 52 TaskPage 의 local dict 패턴 일관.
// cycle 46 학습 phase 5 fallback chain (lang → en → ko) 적용.
const LOGIN_MESSAGES: Record<Lang, {
  heading: string;
  label_company_code: string;
  toggle_company_code: string;
  label_email: string;
  label_password: string;
  placeholder_password: string;
  btn_submit_idle: string;
  divider_sso: string;
  // cycle 96 — 로그인 연동(계정 활성화) 진입 링크
  link_activate: string;
  aria_show_password: string;
  aria_hide_password: string;
  err_invalid_credentials: string;
  err_login_failed: string;
  err_empty: string;
  or: string;
  btn_sso_login: string;
  sso_pending: string;
  // 2026-09-15 — 세션이 끝난 이유(서버 PR #1365 TOKEN_REVOKED · 이 탭의 비밀번호 변경)
  notice_revoked: string;
  notice_password_changed: string;
  // Cycle 54 — 마케팅 텍스트 (좌측 panel, aria-hidden 이라 sr 영향 X)
  marketing_title_line1: string;
  marketing_title_line2: string;
  marketing_subtitle_line1: string;
  marketing_subtitle_line2: string;
  marketing_feature_chat: string;
  marketing_feature_meeting: string;
  marketing_feature_dashboard: string;
}> = {
  ko: {
    heading: '로그인',
    label_company_code: '회사 코드',
    toggle_company_code: '회사 코드로 로그인',
    label_email: '이메일',
    label_password: '비밀번호',
    placeholder_password: '비밀번호 입력',
    btn_submit_idle: '로그인',
    divider_sso: '또는 SSO로 계속',
    link_activate: '로그인 연동 (초기 등록)',
    aria_show_password: '비밀번호 표시',
    aria_hide_password: '비밀번호 숨기기',
    err_invalid_credentials: '이메일 또는 비밀번호가 올바르지 않습니다',
    err_login_failed: '로그인 실패 — 잠시 후 다시 시도하세요',
    err_empty: '이메일과 비밀번호를 입력하세요',
    or: '또는',
    btn_sso_login: '사내 SSO로 로그인',
    sso_pending: '사내 SSO 로그인은 현재 서비스 대기 중입니다.',
    notice_revoked: '보안을 위해 로그아웃되었습니다. 비밀번호 변경 · 모든 기기 로그아웃 · 관리자 조치 뒤에는 다시 로그인해 주세요.',
    notice_password_changed: '비밀번호가 바뀌었습니다. 새 비밀번호로 다시 로그인해 주세요.',
    marketing_title_line1: 'AI 비서와 함께하는',
    marketing_title_line2: '새로운 협업 경험',
    marketing_subtitle_line1: '메신저, 메일, 캘린더, 태스크를 한 곳에서.',
    marketing_subtitle_line2: 'AI가 모든 대화를 이해하고 업무를 도와드립니다.',
    marketing_feature_chat: '모든 채팅에 AI 비서 자동 참여',
    marketing_feature_meeting: '회의록 요약, 휴가 신청 자동화',
    marketing_feature_dashboard: '한눈에 보는 통합 대시보드',
  },
  en: {
    heading: 'Sign in',
    label_company_code: 'Company code',
    toggle_company_code: 'Sign in with company code',
    label_email: 'Email',
    label_password: 'Password',
    placeholder_password: 'Enter your password',
    btn_submit_idle: 'Sign in',
    divider_sso: 'or continue with SSO',
    link_activate: 'Account linking (first-time setup)',
    aria_show_password: 'Show password',
    aria_hide_password: 'Hide password',
    err_invalid_credentials: 'Incorrect email or password',
    err_login_failed: 'Sign-in failed — please try again shortly',
    err_empty: 'Please enter your email and password',
    or: 'or',
    btn_sso_login: 'Sign in with company SSO',
    sso_pending: 'Company SSO sign-in is currently on standby.',
    notice_revoked: 'You were signed out for security. After a password change, a sign-out from all devices, or an admin action, please sign in again.',
    notice_password_changed: 'Your password was changed. Please sign in with your new password.',
    marketing_title_line1: 'A new collaboration experience',
    marketing_title_line2: 'with your AI assistant',
    marketing_subtitle_line1: 'Messenger, mail, calendar, and tasks in one place.',
    marketing_subtitle_line2: 'AI understands every conversation and helps with your work.',
    marketing_feature_chat: 'AI assistant auto-joins every chat',
    marketing_feature_meeting: 'Meeting summaries and leave requests automated',
    marketing_feature_dashboard: 'Unified dashboard at a glance',
  },
  ja: {
    heading: 'ログイン',
    label_company_code: '会社コード',
    toggle_company_code: '会社コードでログイン',
    label_email: 'メール',
    label_password: 'パスワード',
    placeholder_password: 'パスワードを入力',
    btn_submit_idle: 'ログイン',
    divider_sso: 'またはSSOで続行',
    link_activate: 'ログイン連動（初期登録）',
    aria_show_password: 'パスワードを表示',
    aria_hide_password: 'パスワードを非表示',
    err_invalid_credentials: 'メールまたはパスワードが正しくありません',
    err_login_failed: 'ログイン失敗 — しばらくしてから再試行してください',
    err_empty: 'メールとパスワードを入力してください',
    or: 'または',
    btn_sso_login: '社内SSOでログイン',
    sso_pending: '社内SSOログインは現在サービス準備中です。',
    notice_revoked: 'セキュリティのためログアウトしました。パスワード変更・全デバイスからのログアウト・管理者による操作の後は、再度ログインしてください。',
    notice_password_changed: 'パスワードが変更されました。新しいパスワードで再度ログインしてください。',
    marketing_title_line1: 'AIアシスタントと共に',
    marketing_title_line2: '新しいコラボレーション体験',
    marketing_subtitle_line1: 'メッセンジャー、メール、カレンダー、タスクを一つに。',
    marketing_subtitle_line2: 'AIがすべての会話を理解し、業務をサポートします。',
    marketing_feature_chat: 'すべてのチャットにAIアシスタント自動参加',
    marketing_feature_meeting: '議事録要約、休暇申請自動化',
    marketing_feature_dashboard: '一目でわかる統合ダッシュボード',
  },
};

function tLogin(): typeof LOGIN_MESSAGES['ko'] {
  return LOGIN_MESSAGES[getSharedLang()] ?? LOGIN_MESSAGES.en;
}

/**
 * LoginPage (P1-20). 시안 LoginView (WorkChat.jsx line 483~595) 좌우 2단 분해.
 *
 * 좌: MarketingPanel (브랜드 + 3 feature copy) — 모바일에서 hide.
 * 우: LoginForm (CompanyCode + Email + Password Eye toggle + Login + SSO).
 *
 * MFA-pending 응답 분기 → /auth/mfa-verify 에 temp_token 전달 navigate.
 */

interface LoginSuccess {
  // auth v1.5 cookie cutover: 토큰은 Set-Cookie 로 발급됨 (body 노출 X — B1.17 후).
  // 본 단계는 backend 가 body 토큰도 같이 보내지만 frontend 는 사용 X.
  user: {
    id: string;
    email: string;
    name: string;
    role: AuthUser['role'];
    institutionId: string;
    // 로그인 직후 사이드바 아바타 즉시 표시 — 서버가 raw avatarUrl(wcavatar:{ts}/외부 URL) 동봉.
    avatarUrl?: string | null;
  };
}

interface MfaPending {
  mfa_required: true;
  mfa_enroll_required?: boolean;
  temp_token: string;
  temp_token_expires_at: number;
  mfa_methods?: Array<'totp' | 'backup_code'>;
}

type LoginResponse = LoginSuccess | MfaPending;

interface ApiError { code?: string; message?: string }

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const mode = useThemeStore((s) => s.mode);
  const toggleMode = useThemeStore((s) => s.toggleMode);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // 사내 SSO — 현재 서비스 대기중 경고만 표시(기능 미연동).
  const [ssoNotice, setSsoNotice] = useState<string | null>(null);
  /**
   * 세션이 끝난 이유 한 줄 (2026-09-15 · 서버 PR #1365 TOKEN_REVOKED · `lib/session-end-reason.ts`).
   * ★ 읽기는 mount 때 한 번(peek), 지우기는 effect — StrictMode 가 초기화 함수를 두 번 불러도
   *   안내가 사라지지 않고, 다음 방문에는 다시 뜨지 않는다.
   */
  const [sessionEnd] = useState<SessionEndReason | null>(() => peekSessionEndReason());
  useEffect(() => {
    if (sessionEnd) clearSessionEndReason();
  }, [sessionEnd]);

  // FE-W-01: saas 모드일 때만 "회사 가입" 진입 동선 노출.
  // fail-closed — error/loading 시 미노출 (권한 누출 방지).
  const { isSaaS, isLoading: modeLoading } = useDeployMode();

  // 운영 요청 — 로그인 화면 "회사 가입" 진입 동선 히든 처리(노출 차단). 되돌리려면 false.
  const COMPANY_SIGNUP_HIDDEN = true;

  // cycle 96 — 로그인은 이메일+비밀번호만. (사원번호/회사코드는 '로그인 연동' = 계정 활성화 흐름으로 이동.)
  const isValid = email.trim().length > 0 && password.length > 0;

  // 로그인 후 redirect 대상 — AuthGuard 가 보낸 location.state.from 우선.
  const fromState = (location.state as { from?: string } | null)?.from;
  const redirectTo = fromState && fromState !== '/login' ? fromState : '/dashboard';

  async function handleSubmit(e: FormEvent | KeyboardEvent): Promise<void> {
    if ('preventDefault' in e) e.preventDefault();
    // 1회 클릭 후 제출 중이면 두번째 클릭 무시 (이중 제출 차단).
    if (submitting) return;
    // 버튼은 항상 활성 — 빈 값이면 제출하지 않고 안내만(버튼은 계속 활성 유지).
    if (!isValid) {
      setErrorMessage(tLogin().err_empty);
      return;
    }
    setErrorMessage(null);
    setSubmitting(true);
    try {
      // cycle 96 — 로그인은 이메일+비밀번호만. 미등록 계정은 '로그인 연동'(activate) 후 이메일로 로그인.
      const { data } = await apiClient.post<LoginResponse>('/auth/login', {
        email: email.trim(),
        password,
      });

      if ('mfa_required' in data && data.mfa_required) {
        navigate('/auth/mfa-verify', {
          replace: true,
          state: {
            tempToken: data.temp_token,
            tempTokenExpiresAt: data.temp_token_expires_at,
            mfaEnrollRequired: data.mfa_enroll_required ?? false,
            mfaMethods: data.mfa_methods ?? ['totp'],
            redirectTo,
          },
        });
        return;
      }

      const success = data as LoginSuccess;
      setSession({
        id: Number(success.user.id),
        email: success.user.email,
        name: success.user.name,
        role: success.user.role,
        institutionId: Number(success.user.institutionId),
        avatarUrl: success.user.avatarUrl ?? null,
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const ax = err as AxiosError<ApiError>;
      const code = ax.response?.data?.code;
      const message = ax.response?.data?.message;
      const t = tLogin();
      setErrorMessage(
        message ??
          (code === 'INVALID_CREDENTIALS'
            ? t.err_invalid_credentials
            : t.err_login_failed),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleEnter(ev: KeyboardEvent<HTMLInputElement>): void {
    // Enter 도 버튼과 동일 동작 — 제출 중이 아니면 handleSubmit(빈 값 안내 포함) 위임.
    if (ev.key === 'Enter' && !submitting) {
      void handleSubmit(ev);
    }
  }

  // 사내 SSO 로그인 — 아직 미연동. 클릭 시 "서비스 대기중" 경고만 반환.
  function handleSso(): void {
    setErrorMessage(null);
    setSsoNotice(tLogin().sso_pending);
  }

  // DEV 전용 — 시드 계정(admin@dev.workchat.kr / DEV-001 / Dev@1234!)으로 즉시 로그인("이미 인증된 것처럼").
  // import.meta.env.DEV 게이트 → production 빌드에서는 함수/버튼 모두 미포함(트리쉐이킹). 실제 /auth/login 을
  // 태워 정상 JWT 세션을 발급하므로 백엔드 API(JWT 필수)도 그대로 동작. 인증 우회가 아니라 dev 자동입력.
  async function handleDevLogin(): Promise<void> {
    if (submitting) return;
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const { data } = await apiClient.post<LoginResponse>('/auth/login', {
        email: 'admin@dev.workchat.kr',
        password: 'Dev@1234!',
        institutionCode: 'DEV-001',
      });
      if ('mfa_required' in data && data.mfa_required) {
        navigate('/auth/mfa-verify', {
          replace: true,
          state: {
            tempToken: data.temp_token,
            tempTokenExpiresAt: data.temp_token_expires_at,
            mfaEnrollRequired: data.mfa_enroll_required ?? false,
            mfaMethods: data.mfa_methods ?? ['totp'],
            redirectTo,
          },
        });
        return;
      }
      const success = data as LoginSuccess;
      setSession({
        id: Number(success.user.id),
        email: success.user.email,
        name: success.user.name,
        role: success.user.role,
        institutionId: Number(success.user.institutionId),
        avatarUrl: success.user.avatarUrl ?? null,
      });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const ax = err as AxiosError<ApiError>;
      setErrorMessage(
        ax.response?.data?.message ??
          'DEV 테스트 로그인 실패 — 시드 계정/마이그레이션(employee_number) 확인 필요',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      {/* 좌측 — 확정 시안 ⑧(미니멀 센터) 애니메이션 데모. self-contained iframe(srcDoc)로 임베드해
          앱 전역 CSS 와 격리(클래스 충돌 0). 장식 전용(aria-hidden) · 모바일(≤768px)에서는 CSS 로 숨김. */}
      <aside className="login-marketing" aria-hidden="true">
        <iframe
          className="login-marketing__demo"
          title="Axi 데모"
          aria-hidden="true"
          tabIndex={-1}
          scrolling="no"
          srcDoc={LOGIN_DEMO_HTML}
        />
      </aside>

      <section className="login-panel">
        <button
          type="button"
          className="login-panel__theme-toggle"
          onClick={toggleMode}
          aria-label={mode === 'dark' ? '라이트 모드로' : '다크 모드로'}
          title={mode === 'dark' ? '라이트 모드로' : '다크 모드로'}
        >
          {mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </button>

        <div className="login-panel__inner">
          <div className="login-panel__brand">
            <span className="login-panel__brand-mark">A</span>
            <span className="login-panel__brand-name">AXI Works</span>
          </div>
          {(() => {
            const t = tLogin();
            return (
              <>
          <h2 className="login-panel__heading">{t.heading}</h2>
          <p className="login-panel__subheading">AXI Works 계정으로 로그인하세요</p>

          {sessionEnd && (
            <p className="login-form__notice" role="status" data-testid="login-session-end">
              <Info size={15} aria-hidden />
              <span>{sessionEnd === 'password-changed' ? t.notice_password_changed : t.notice_revoked}</span>
            </p>
          )}

          {errorMessage && (
            <div role="alert" className="login-form__error">
              <span>{errorMessage}</span>
            </div>
          )}

          <form onSubmit={(e) => void handleSubmit(e)} noValidate>
            <div className="login-form__field">
              <label htmlFor="email" className="login-form__label">{t.label_email}</label>
              <div className="login-form__input-wrapper">
                <AtSign size={16} className="login-form__icon-left" />
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  className="login-form__input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleEnter}
                  placeholder="name@company.com"
                  required
                />
              </div>
            </div>

            <div className="login-form__field">
              <label htmlFor="password" className="login-form__label">{t.label_password}</label>
              <div className="login-form__input-wrapper">
                <Lock size={16} className="login-form__icon-left" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="login-form__input login-form__input--with-action"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleEnter}
                  placeholder={t.placeholder_password}
                  required
                />
                <button
                  type="button"
                  className="login-form__icon-right"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t.aria_hide_password : t.aria_show_password}
                  title={showPassword ? t.aria_hide_password : t.aria_show_password}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* cycle 96 — 로그인 연동(계정 활성화) 진입. 아직 연동 안 된 계정은 HR 발급 사원번호+이메일+
                초기 비밀번호로 1회 연동 후, 이후 이메일+비밀번호로 로그인한다. */}
            <Link to="/activate" className="login-form__company-code-toggle">
              {t.link_activate}
            </Link>

            <button
              type="submit"
              className="login-form__button login-form__button--primary"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="login-form__spinner" /> 로그인 중...
                </>
              ) : (
                t.btn_submit_idle
              )}
            </button>
          </form>

          {/* 시안 — 로그인 버튼 아래 사내 SSO 버튼(현재 서비스 대기중, 기능 미연동). */}
          <div className="login-form__divider">
            <span className="login-form__divider-line" />
            <span className="login-form__divider-label">{t.or}</span>
            <span className="login-form__divider-line" />
          </div>
          <button
            type="button"
            className="login-form__button login-form__button--sso"
            onClick={handleSso}
          >
            <Key size={16} /> {t.btn_sso_login}
          </button>
          {ssoNotice && (
            <p className="login-form__sso-notice" role="status">
              <AlertTriangle size={15} /> {ssoNotice}
            </p>
          )}
              </>
            );
          })()}

          {/* SSO 로그인은 사용자 결정으로 '계획에서 삭제' (2026-05-23) — 로그인 화면 미노출.
              🚫 복원 금지: P1-07~09 plan 항목이 있어도 SSO 로그인 버튼/divider 를 다시 추가하지 않는다.
              (백엔드 SSO 연동 인프라 c87-90 + Settings 의 SSO 계정 연동은 보존 — 로그인 진입점만 제거.) */}

          {/* DEV 전용 테스트 로그인 — 실제 인증(위 '로그인')은 인증 연동 경로로 유지하고, 테스트는 1클릭 진입.
              import.meta.env.DEV 게이트 → production 빌드 미노출(트리쉐이킹). */}
          {import.meta.env.DEV && (
            <div className="login-form__sso-row">
              <button
                type="button"
                className="login-form__button login-form__button--secondary"
                onClick={() => void handleDevLogin()}
                disabled={submitting}
                title="DEV 전용 — 시드 계정(admin@dev.workchat.kr / DEV-001)으로 즉시 로그인. production 빌드 미노출."
              >
                🧪 테스트 로그인 (DEV)
              </button>
            </div>
          )}

          {/* FE-W-01 — saas 모드에서만 "회사 가입" 진입. loading 중 skeleton 으로 CLS 차단. */}
          {COMPANY_SIGNUP_HIDDEN ? null : modeLoading ? (
            <div
              className="login-form__signup-skeleton"
              aria-hidden="true"
              style={{
                marginTop: '1.5rem',
                height: '1.2rem',
                borderRadius: '4px',
                background: 'var(--color-bg-elevated, rgba(0,0,0,0.04))',
              }}
            />
          ) : isSaaS ? (
            <p
              className="login-form__signup-cta"
              style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.875rem' }}
            >
              <span style={{ color: 'var(--color-text-secondary, #6b7280)' }}>
                회사 계정이 아직 없으신가요?{' '}
              </span>
              <Link
                to="/signup"
                aria-label="새로운 회사 가입 시작"
                style={{ fontWeight: 600 }}
              >
                회사 가입
              </Link>
            </p>
          ) : null}

          {/* 확정 시안 우측 폼 하단 — 업무 태그라인 + 회사 정보 푸터 (시안 그대로 적용) */}
          <div className="login-panel__mvoice">
            <MessageSquare size={16} />
            <span>PC는 <b>텍스트</b>, 휴대폰은 <b>음성</b>으로 — 어디서나 대화로 일처리</span>
          </div>
          <div className="login-panel__company">
            <b>(주)타임소프트</b>
            <div>서울 금천구 가산디지털1로 24, 504호 (대륭테크노타워 13차)</div>
            <div className="login-panel__company-tf">T. 02.2029.5060&nbsp;&nbsp;F. 02.2029.5061</div>
            {/* oss-notices — 배포본 수령자 누구나 접근 가능해야 하므로 로그인 전에도 노출. */}
            <div className="login-panel__company-oss">
              <Link to="/oss-licenses">{tOss('oss.link')}</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
